import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { findValueInEnum, maybe } from "@saleor/misc";
import {
  ProductFilterKeys,
  ProductInventory,
  ProductListFilterOpts,
  ProductVisibility
} from "@saleor/products/components/ProductListPage";
import useProductUserType from "@saleor/products/hooks/useProductUserType";
import {
  InitialProductFilterData_attributes_edges_node,
  InitialProductFilterData_categories_edges_node
} from "@saleor/products/types/InitialProductFilterData";
import {
  SearchCategories,
  SearchCategoriesVariables
} from "@saleor/searches/types/SearchCategories";
import isArray from "lodash-es/isArray";

import { IFilterElement } from "../../../components/Filter";
import {
  ProductFilterInput,
  ProductStatusEnum,
  ProductUserViewEnum
} from "../../../types/globalTypes";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getMultipleValueQueryParam,
  getSingleEnumValueQueryParam
} from "../../../utils/filters";
import {
  ProductListUrlFilters,
  ProductListUrlFiltersAsDictWithMultipleValues,
  ProductListUrlFiltersEnum,
  ProductListUrlFiltersWithMultipleValues,
  ProductListUrlQueryParams
} from "../../urls";

export const PRODUCT_FILTERS_KEY = "productFilters";

export function getFilterOpts(
  params: ProductListUrlFilters,
  _attributes: InitialProductFilterData_attributes_edges_node[],
  categories: {
    initial: InitialProductFilterData_categories_edges_node[];
    search: UseSearchResult<SearchCategories, SearchCategoriesVariables>;
  }
): ProductListFilterOpts {
  return {
    categories: {
      active:
        !!params.categories &&
        Array.isArray(params.categories) &&
        params.categories.length > 0,
      choices: maybe(
        () =>
          categories.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.categories
        ? maybe(
            () =>
              categories.initial.map(category => ({
                label: category.name,
                value: category.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => categories.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: categories.search.result.loading,
      onFetchMore: categories.search.loadMore,
      onSearchChange: categories.search.search,
      value: maybe(() => dedupeFilter(params.categories), [])
    },
    productInventory: {
      active: maybe(() => params.inventory !== undefined, false),
      value: maybe(() => findValueInEnum(params.inventory, ProductInventory))
    },
    productStatus: {
      active: maybe(() => params.productStatus !== undefined, false),
      value: maybe(() =>
        findValueInEnum(params.productStatus, ProductStatusEnum)
      )
    },
    productVisibility: {
      active: maybe(() => params.visibility !== undefined, false),
      value: maybe(() => findValueInEnum(params.visibility, ProductVisibility))
    }
  };
}

function getProductVisibilityFilterVars(params) {
  const user = useProductUserType(params);
  const isStoreAdmin =
    user.isKAMS === false && user.userType === ProductUserViewEnum.STORE_ADMIN;
  const isKAMSStoreAdmin =
    user.isKAMS && user.userType === ProductUserViewEnum.STORE_ADMIN;

  return {
    ...(isStoreAdmin &&
      params.visibility !== undefined && {
        isVisible: {
          isVisible:
            params.visibility !== undefined
              ? params.visibility === ProductVisibility.PUBLISHED
              : null
        }
      }),
    ...(isKAMSStoreAdmin &&
      params.visibility !== undefined && {
        isVisible: {
          isVisible:
            params.visibility !== undefined
              ? params.visibility === ProductVisibility.PUBLISHED
              : null,
          tenant: params.tenant ? params.tenant : ""
        }
      })
  };
}

function getProductStatusFilterVars(params) {
  const user = useProductUserType(params);
  const isSellerMaster =
    user.isKAMS === false &&
    user.userType === ProductUserViewEnum.SELLER_MASTER;
  const isKAMSSellerMaster =
    user.isKAMS && user.userType === ProductUserViewEnum.SELLER_MASTER;
  const isSuperUser = user.isSuperUser;

  return {
    ...((isSellerMaster || isKAMSSellerMaster || isSuperUser) && {
      productStatus:
        params.productStatus !== undefined
          ? findValueInEnum(params.productStatus, ProductStatusEnum)
          : null
    })
  };
}

export function getFilterVariables(
  params: ProductListUrlFilters,
  warehouseIds?: string[]
): ProductFilterInput {
  return {
    attributes: !!params.attributes
      ? Object.keys(params.attributes).map(key => ({
          slug: key,
          // It is possible for qs to parse values not as string[] but string
          values: isArray(params.attributes[key])
            ? params.attributes[key]
            : (([params.attributes[key]] as unknown) as string[])
        }))
      : null,
    categories: params.categories !== undefined ? params.categories : null,
    ...getProductVisibilityFilterVars(params),
    ...getProductStatusFilterVars(params),
    search: !params.searchBy ? params.query : null,
    searchByName: params.searchBy === "NAME" ? params.query : "",
    searchBySku: params.searchBy === "SKU" ? params.query : "",
    ...(params.inventory && {
      stocks: {
        warehouseIds,
        ...(params.inventory === "inStock" && {
          quantity: {
            gte: 1
          }
        }),
        ...(params.inventory === "outOfStock" && {
          quantity: {
            lte: 0
          }
        })
      }
    })
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<ProductFilterKeys>,
  params: ProductListUrlFilters
): ProductListUrlFilters {
  const { active, group, name, value } = filter;

  if (!!group) {
    const rest = params && params[group] ? params[group] : undefined;

    return {
      [group]: active
        ? {
            ...(rest === undefined ? {} : rest),
            [name]: value
          }
        : rest
    };
  }

  switch (name) {
    case ProductFilterKeys.productStatus:
      return getSingleEnumValueQueryParam(
        filter,
        ProductListUrlFiltersEnum.productStatus,
        ProductStatusEnum
      );

    case ProductFilterKeys.categories:
      return getMultipleValueQueryParam(
        filter,
        ProductListUrlFiltersWithMultipleValues.categories
      );

    case ProductFilterKeys.productVisibility:
      return getSingleEnumValueQueryParam(
        filter,
        ProductListUrlFiltersEnum.visibility,
        ProductVisibility
      );

    case ProductFilterKeys.productInventory:
      return getSingleEnumValueQueryParam(
        filter,
        ProductListUrlFiltersEnum.inventory,
        ProductInventory
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<ProductListUrlFilters>(PRODUCT_FILTERS_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  ProductListUrlQueryParams,
  ProductListUrlFilters
>({
  ...ProductListUrlFiltersEnum,
  ...ProductListUrlFiltersWithMultipleValues,
  ...ProductListUrlFiltersAsDictWithMultipleValues
});
